import { ColumnDef, Row } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { IconButtonCell, TextCell } from '@pages/Admin/Reports/Payments/components/Cells/Cells'
import { IFinancialTransactionsReportItem } from '../utils/types'
import cn from './index.module.css'
import classNames from 'classnames'
import { formatPaymentEntityLabel } from '@/helpers/paymentEntities'
import { formatPaymentDiscount } from '@/helpers/paymentDiscounts'
import { DeleteOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'

const formatPaymentPeriodDate = (value?: string | null) => {
  if (!value || value === '-' || value === 'Навсегда') {
    return value ?? '-'
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value
}

const useTableColumns = (
  canRemove: boolean,
  onRemove: (paymentRowId: number) => void,
) => {
  const settings = useSelector((store: any) => store.settings)
  const showGroupName =
    settings?.allowStudentToEnrollSameCourseInDifferentGroups === '1'

  return useMemo<ColumnDef<IFinancialTransactionsReportItem>[]>(() => {
    const allColumns = [
      {
        header: '',
        id: 'index',
        size: 50,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => (
          <TextCell
            text={((row.original.index ?? 0) + 1)?.toString() ?? '-'}
            styles={{ textAlign: 'center' }}
          />
        ),
      },
      {
        header: 'Курс/Класс/Баланс',
        id: 'course',
        size: 250,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => {
          const groupName =
            row.original?.group?.name ?? row.original?.group_name ?? ''
          const courseSubtitle = row.original?.course_subtitle ?? ''

          return (
            <div style={{ padding: '12px', width: '100%' }}>
              <div
                style={{
                  width: '100%',
                  textAlign: 'start',
                }}
              >
                {formatPaymentEntityLabel(
                  row.original.entity_type ?? 'course',
                  row.original.course,
                )}
              </div>
              {!!courseSubtitle && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: 'var(--color-text-soft)',
                    textAlign: 'start',
                  }}
                >
                  {courseSubtitle}
                </div>
              )}
              {showGroupName && !!groupName && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: 'var(--color-text-soft)',
                    textAlign: 'start',
                  }}
                >
                  Группа: {groupName}
                </div>
              )}
            </div>
          )
        },
      },
      {
        header: 'Сумма',
        id: 'amount',
        size: 100,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => (
          <TextCell text={String(row.original.amount ?? '-')} />
        ),
      },
      {
        header: 'Скидка',
        id: 'promoDiscountAmount',
        size: 150,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => (
          <TextCell
            text={formatPaymentDiscount(row.original)}
            styles={{ whiteSpace: 'pre-line' }}
          />
        ),
      },
      {
        header: 'Дата',
        id: 'date',
        size: 100,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => (
          <TextCell text={row.original.date ?? '-'} />
        ),
      },
      {
        header: 'Продлено/списано на',
        id: 'accessExtendedBy',
        size: 150,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => (
          <TextCell text={row.original.accessExtendedBy ?? '-'} />
        ),
      },
      {
        header: 'Оплачено с',
        id: 'paidFrom',
        size: 100,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => (
          <TextCell text={formatPaymentPeriodDate(row.original.paidFrom)} />
        ),
      },
      {
        header: 'Оплачено до',
        id: 'paidTo',
        size: 100,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => (
          <TextCell text={formatPaymentPeriodDate(row.original.paidTo)} />
        ),
      },
      {
        header: 'Формат оплаты',
        id: 'paymentFormat',
        size: 150,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => (
          <TextCell text={row.original.paymentFormat ?? '-'} />
        ),
      },
      {
        header: 'Статус',
        id: 'status',
        size: 100,
        cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) => {
          const status = row.original.status ?? '-'
          return (
            <TextCell
              text={status}
              className={classNames(
                status === 'Списано' && cn.statusWithdrawn,
                status === 'Оплачено' && cn.statusPaid,
                status === 'Возвращено' && cn.statusRefunded,
              )}
            />
          )
        },
      },
      ...(canRemove
        ? [
            {
              header: '',
              id: 'remove',
              size: 50,
              cell: ({ row }: { row: Row<IFinancialTransactionsReportItem> }) =>
                row.original.paymentRowId ? (
                  <Tooltip title="Удалить строку">
                    <span>
                      <IconButtonCell
                        icon={
                          <DeleteOutlined
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          />
                        }
                        onClick={() => onRemove(Number(row.original.paymentRowId))}
                      />
                    </span>
                  </Tooltip>
                ) : null,
            },
          ]
        : []),
    ]

    return allColumns
  }, [showGroupName, canRemove, onRemove])
}

export default useTableColumns
